import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/lib/api";
import type { ScopeDefinition, Severity, Situation, SituationStatus } from "@/lib/types";
import {
  buildSituationPayload,
  formatSaoPaulo,
  GUIDANCE_LINE_MAX,
  isoToSaoPauloLocal,
  newSituationId,
  NOTE_MAX,
  SUMMARY_MAX,
  type SituationFormErrors,
  type SituationFormValues,
} from "@/lib/situations";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Badge, type Tone } from "@/components/ui/badge";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, Input, Select, Textarea } from "@/components/ui/field";
import { ErrorState, Loading } from "@/components/query-state";

const SEVERITY_TONE: Record<Severity, Tone> = { high: "danger", medium: "warning", low: "info" };
const PAGE_SIZE = 50;
const READBACK_TRIES = 10;
const READBACK_DELAY_MS = 1000;

function scopeValues(scope: Situation["scope"]): Record<string, string[]> {
  return Object.fromEntries(
    Object.entries(scope).map(([key, value]) => [key, Array.isArray(value) ? value : [value]]),
  );
}

function ScopeChips({ scope, catalog }: { scope: Situation["scope"]; catalog: ScopeDefinition[] | undefined }) {
  const { t } = useTranslation();
  const entries = Object.entries(scopeValues(scope));
  if (entries.length === 0) return <Badge tone="danger">{t("situations.global")}</Badge>;
  return (
    <span className="flex flex-wrap gap-1">
      {entries.flatMap(([key, values]) =>
        values.map((value) => {
          const label = catalog?.find((s) => s.key === key)?.values.find((v) => v.value === value)?.label ?? `${key}:${value}`;
          return <Badge key={`${key}:${value}`}>{label}</Badge>;
        }),
      )}
    </span>
  );
}

function StatusBadge({ situation }: { situation: Situation }) {
  const { t } = useTranslation();
  if (situation.status === "active") {
    const scheduled = Date.parse(situation.starts_at) > Date.now();
    return <Badge tone={scheduled ? "accent" : "success"}>{t(scheduled ? "situations.status.scheduled" : "situations.status.active")}</Badge>;
  }
  return <Badge>{t(situation.resolved_reason === "expired" ? "situations.status.expired" : "situations.status.resolved")}</Badge>;
}

export function SituationsListPage() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<SituationStatus | "">("active");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const scopes = useQuery({ queryKey: ["scopes"], queryFn: api.scopes });
  const list = useQuery({
    queryKey: ["situations", status, q, offset],
    queryFn: () => api.situations.list({ status: status || undefined, q: q || undefined, offset }),
  });

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setOffset(0);
    setQ(search.trim());
  };

  return (
    <div>
      <PageHeader
        title={t("situations.title")}
        description={t("situations.description")}
        sacredIcon="dove"
        actions={list.data?.can_write ? <Link to="/situations/new" className="bg-accent text-accent-fg hover:bg-accent-hover inline-flex h-10 items-center rounded-md px-4 text-sm font-medium">{t("situations.create")}</Link> : undefined}
      />
      {list.data && !list.data.can_write ? <p className="text-muted mb-4 text-sm">{t("situations.readOnly")}</p> : null}
      <form onSubmit={submitSearch} className="mb-4 grid gap-3 sm:grid-cols-[12rem_1fr_auto] sm:items-end">
        <Field label={t("situations.filterStatus")} htmlFor="situation-status">
          <Select id="situation-status" value={status} onChange={(e) => { setOffset(0); setStatus(e.target.value as SituationStatus | ""); }}>
            <option value="active">{t("situations.filter.active")}</option>
            <option value="resolved">{t("situations.filter.resolved")}</option>
            <option value="">{t("common.all")}</option>
          </Select>
        </Field>
        <Field label={t("situations.search")} htmlFor="situation-search">
          <Input id="situation-search" type="search" maxLength={100} value={search} onChange={(e) => setSearch(e.target.value)} />
        </Field>
        <Button type="submit" variant="secondary">{t("situations.searchButton")}</Button>
      </form>
      {list.isLoading ? <Loading /> : list.error ? <ErrorState error={list.error} /> : list.data && list.data.items.length === 0 ? (
        <div className="border-border bg-surface rounded-xl border p-8 text-center">
          <p className="text-muted text-sm">{t("situations.empty")}</p>
        </div>
      ) : list.data ? (
        <>
          <p className="text-muted mb-2 text-sm">{t("situations.count", { count: list.data.total })}</p>
          <div className="border-border bg-surface overflow-x-auto rounded-xl border">
            <table className="w-full text-left text-sm">
              <thead className="text-muted text-xs">
                <tr>
                  <th className="px-3 py-2">{t("situations.colSeverity")}</th>
                  <th className="px-3 py-2">{t("situations.colSummary")}</th>
                  <th className="px-3 py-2">{t("situations.colAudience")}</th>
                  <th className="px-3 py-2">{t("situations.colValidity")}</th>
                  <th className="px-3 py-2">{t("situations.colStatus")}</th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((s) => (
                  <tr key={s.situation_id} className="border-border-subtle border-t align-top">
                    <td className="px-3 py-2"><Badge tone={SEVERITY_TONE[s.severity]}>{t(`situations.severity.${s.severity}`)}</Badge></td>
                    <td className="min-w-0 px-3 py-2"><Link className="text-foreground hover:underline" to={`/situations/${s.situation_id}`}>{s.summary}</Link></td>
                    <td className="px-3 py-2"><ScopeChips scope={s.scope} catalog={scopes.data} /></td>
                    <td className="px-3 py-2 whitespace-nowrap tabular-nums">{t("situations.until", { date: formatSaoPaulo(s.expires_at) })}</td>
                    <td className="px-3 py-2"><StatusBadge situation={s} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-center justify-end gap-2">
            <Button variant="secondary" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>{t("situations.previous")}</Button>
            <Button variant="secondary" size="sm" disabled={offset + PAGE_SIZE >= list.data.total} onClick={() => setOffset(offset + PAGE_SIZE)}>{t("situations.next")}</Button>
          </div>
        </>
      ) : null}
    </div>
  );
}

function defaultExpiry(): string {
  return isoToSaoPauloLocal(new Date(Date.now() + 6 * 3600_000).toISOString());
}

function valuesFrom(situation: Situation | undefined): SituationFormValues {
  if (!situation) {
    return { severity: "medium", summary: "", guidance: "", scope: {}, global: false, startsAt: "", expiresAt: defaultExpiry(), eta: "", internalNote: "", policyRef: "" };
  }
  const scope = scopeValues(situation.scope);
  return {
    severity: situation.severity,
    summary: situation.summary,
    guidance: situation.guidance.join("\n"),
    scope,
    global: Object.keys(scope).length === 0,
    startsAt: isoToSaoPauloLocal(situation.starts_at),
    expiresAt: isoToSaoPauloLocal(situation.expires_at),
    eta: situation.eta ? isoToSaoPauloLocal(situation.eta) : "",
    internalNote: situation.internal_note ?? "",
    policyRef: situation.policy_ref ?? "",
  };
}

/** The write is async (202): read the situation back until the expected revision is stored. */
async function readBack(id: string, minRevision: number): Promise<boolean> {
  for (let attempt = 0; attempt < READBACK_TRIES; attempt += 1) {
    try {
      const stored = await api.situations.get(id);
      if (stored.revision >= minRevision) return true;
    } catch (error) {
      if (!(error instanceof ApiError && error.status === 404)) throw error;
    }
    await new Promise((resolve) => setTimeout(resolve, READBACK_DELAY_MS));
  }
  return false;
}

function writeError(error: unknown, t: (key: string) => string): string {
  if (error instanceof ApiError) {
    if (error.status === 409) return t("situations.conflict");
    if (error.forbidden) return t("situations.forbidden");
    if (error.status === 400) return error.message;
  }
  return t("situations.writeFailed");
}

export function SituationEditorPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const editing = Boolean(id);
  // Loaded once: a background refetch must not reset unsaved edits.
  const current = useQuery({
    queryKey: ["situation", id],
    queryFn: () => api.situations.get(id!),
    enabled: editing,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const access = useQuery({
    queryKey: ["situations", "active", "", 0],
    queryFn: () => api.situations.list({ status: "active" }),
    enabled: !editing,
  });
  const state = editing ? current : access;
  if (state.isLoading) return <Loading />;
  if (state.error) return <ErrorState error={state.error} />;
  const canWrite = editing ? current.data?.can_write : access.data?.can_write;
  if (!canWrite) return <p className="text-muted p-10 text-center text-sm">{t("situations.readOnly")}</p>;
  if (current.data && current.data.status !== "active") {
    return <p className="text-muted p-10 text-center text-sm">{t("situations.notEditable")}</p>;
  }
  return <SituationForm key={current.data?.situation_id ?? "new"} current={current.data} />;
}

function SituationForm({ current }: { current: Situation | undefined }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const scopes = useQuery({ queryKey: ["scopes"], queryFn: api.scopes });
  const [values, setValues] = useState<SituationFormValues>(() => valuesFrom(current));
  const [errors, setErrors] = useState<SituationFormErrors>({});
  const [notice, setNotice] = useState<string | null>(null);
  // One id per editor session, so a retried create is a replay, not a second situation.
  const situationId = useRef(current?.situation_id ?? newSituationId());
  // The revision this editor started from; a newer one on the server must answer 409.
  const baseRevision = useRef(current?.revision ?? 0);
  const set = <K extends keyof SituationFormValues>(key: K, value: SituationFormValues[K]) => setValues((v) => ({ ...v, [key]: value }));

  const save = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (current) {
        const result = await api.situations.update(current.situation_id, { ...payload, expected_revision: baseRevision.current });
        return { id: current.situation_id, revision: result.revision ?? baseRevision.current + 1 };
      }
      const result = await api.situations.create({ ...payload, situation_id: situationId.current });
      return { id: result.situation_id, revision: result.revision ?? 1 };
    },
    onSuccess: async ({ id, revision }) => {
      setNotice(t("situations.waiting"));
      const stored = await readBack(id, revision).catch(() => false);
      await queryClient.invalidateQueries({ queryKey: ["situations"] });
      await queryClient.invalidateQueries({ queryKey: ["situation", id] });
      if (stored) navigate(`/situations/${id}`);
      else setNotice(t("situations.stillProcessing"));
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const result = buildSituationPayload(values, { clearEmpty: Boolean(current) });
    if (!result.ok) { setErrors(result.errors); return; }
    setErrors({});
    save.mutate(result.payload);
  };

  const toggleValue = (key: string, value: string, checked: boolean) => {
    const selected = values.scope[key] ?? [];
    set("scope", { ...values.scope, [key]: checked ? [...selected, value] : selected.filter((v) => v !== value) });
  };
  const fieldError = (key: keyof SituationFormValues) => errors[key] ? <p className="text-danger text-xs" role="alert">{t(errors[key]!)}</p> : null;

  return (
    <form onSubmit={submit} noValidate>
      <PageHeader title={t(current ? "situations.editTitle" : "situations.newTitle")} description={t("situations.editorDescription")} sacredIcon="quill" />
      <div className="grid gap-4">
        <Card>
          <CardHeader><CardTitle sacredIcon="scroll">{t("situations.sectionMessage")}</CardTitle></CardHeader>
          <CardBody className="grid gap-4">
            <Field label={t("situations.severityLabel")} htmlFor="situation-severity" hint={t("situations.severityHint")}>
              <Select id="situation-severity" value={values.severity} onChange={(e) => set("severity", e.target.value as Severity)}>
                {(["high", "medium", "low"] as const).map((s) => <option key={s} value={s}>{t(`situations.severity.${s}`)}</option>)}
              </Select>
            </Field>
            <Field label={t("situations.summary")} htmlFor="situation-summary" hint={t("situations.summaryHint", { count: values.summary.trim().length, max: SUMMARY_MAX })}>
              <Textarea id="situation-summary" maxLength={SUMMARY_MAX} value={values.summary} onChange={(e) => set("summary", e.target.value)} />
            </Field>
            {fieldError("summary")}
            <Field label={t("situations.guidance")} htmlFor="situation-guidance" hint={t("situations.guidanceHint", { max: GUIDANCE_LINE_MAX })}>
              <Textarea id="situation-guidance" value={values.guidance} onChange={(e) => set("guidance", e.target.value)} />
            </Field>
            {fieldError("guidance")}
          </CardBody>
        </Card>

        <Card>
          <CardHeader><CardTitle sacredIcon="keys">{t("situations.sectionAudience")}</CardTitle></CardHeader>
          <CardBody className="grid gap-4">
            <p className="text-muted text-sm">{t("situations.audienceHint")}</p>
            {scopes.isLoading ? <Loading /> : scopes.error ? <ErrorState error={scopes.error} /> : (scopes.data ?? []).map((scope) => (
              <fieldset key={scope.key} disabled={values.global} className="grid gap-2">
                <legend className="text-secondary mb-1 text-sm font-medium">{scope.label}</legend>
                <div className="flex flex-wrap gap-3">
                  {scope.values.map((option) => {
                    const inputId = `scope-${scope.key}-${option.value}`;
                    return (
                      <label key={option.value} htmlFor={inputId} className="inline-flex items-center gap-2 text-sm">
                        <input id={inputId} type="checkbox" checked={(values.scope[scope.key] ?? []).includes(option.value)} onChange={(e) => toggleValue(scope.key, option.value, e.target.checked)} />
                        {option.label}
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            ))}
            <label htmlFor="situation-global" className="border-danger/40 bg-danger-soft inline-flex items-start gap-2 rounded-md border p-3 text-sm">
              <input id="situation-global" type="checkbox" checked={values.global} onChange={(e) => setValues((v) => ({ ...v, global: e.target.checked, scope: e.target.checked ? {} : v.scope }))} />
              <span>{t("situations.globalLabel")}</span>
            </label>
            {values.global ? <p className="text-danger text-sm">{t("situations.globalWarning")}</p> : null}
            {fieldError("scope")}
          </CardBody>
        </Card>

        <Card>
          <CardHeader><CardTitle sacredIcon="seal">{t("situations.sectionValidity")}</CardTitle></CardHeader>
          <CardBody className="grid gap-4 sm:grid-cols-3">
            <div className="grid gap-1">
              <Field label={t("situations.startsAt")} htmlFor="situation-starts" hint={t("situations.startsHint")}>
                <Input id="situation-starts" type="datetime-local" value={values.startsAt} onChange={(e) => set("startsAt", e.target.value)} />
              </Field>
              {fieldError("startsAt")}
            </div>
            <div className="grid gap-1">
              <Field label={t("situations.expiresAt")} htmlFor="situation-expires" hint={t("situations.expiresHint")}>
                <Input id="situation-expires" type="datetime-local" value={values.expiresAt} onChange={(e) => set("expiresAt", e.target.value)} />
              </Field>
              {fieldError("expiresAt")}
            </div>
            <div className="grid gap-1">
              <Field label={t("situations.eta")} htmlFor="situation-eta" hint={t("situations.etaHint")}>
                <Input id="situation-eta" type="datetime-local" value={values.eta} onChange={(e) => set("eta", e.target.value)} />
              </Field>
              {fieldError("eta")}
            </div>
            <p className="text-muted text-xs sm:col-span-3">{t("situations.timezoneNote")}</p>
          </CardBody>
        </Card>

        <Card>
          <CardHeader><CardTitle sacredIcon="columns">{t("situations.sectionInternal")}</CardTitle></CardHeader>
          <CardBody className="grid gap-4">
            <Field label={t("situations.internalNote")} htmlFor="situation-note" hint={t("situations.internalNoteHint", { max: NOTE_MAX })}>
              <Textarea id="situation-note" maxLength={NOTE_MAX} value={values.internalNote} onChange={(e) => set("internalNote", e.target.value)} />
            </Field>
            {fieldError("internalNote")}
            <Field label={t("situations.policyRef")} htmlFor="situation-policy" hint={t("situations.policyRefHint")}>
              <Input id="situation-policy" maxLength={200} value={values.policyRef} onChange={(e) => set("policyRef", e.target.value)} />
            </Field>
          </CardBody>
        </Card>

        {save.error ? <p role="alert" className="border-danger/40 bg-danger-soft text-danger rounded-md border p-3 text-sm">{writeError(save.error, t)}</p> : null}
        {notice ? <p role="status" className="text-muted text-sm">{notice}</p> : null}
        <div className="flex flex-wrap justify-end gap-2">
          <Link to={current ? `/situations/${current.situation_id}` : "/situations"} className="text-secondary hover:bg-surface-sunken inline-flex h-10 items-center rounded-md px-4 text-sm">{t("common.cancel")}</Link>
          <Button type="submit" disabled={save.isPending}>{t(current ? "situations.saveChanges" : "situations.open")}</Button>
        </div>
      </div>
    </form>
  );
}

const REVISION_TYPE_KEY: Record<string, string> = {
  "vammo.situations.situation.opened.v1": "situations.revision.opened",
  "vammo.situations.situation.updated.v1": "situations.revision.updated",
  "vammo.situations.situation.resolved.v1": "situations.revision.resolved",
};

export function SituationDetailPage() {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const situation = useQuery({ queryKey: ["situation", id], queryFn: () => api.situations.get(id) });
  const revisions = useQuery({ queryKey: ["situation", id, "revisions"], queryFn: () => api.situations.revisions(id) });
  const scopes = useQuery({ queryKey: ["scopes"], queryFn: api.scopes });

  const resolve = useMutation({
    mutationFn: (revision: number) => api.situations.resolve(id, revision),
    onSuccess: async (result) => {
      setConfirming(false);
      setNotice(t("situations.resolveSent"));
      await readBack(id, result.revision ?? 0).catch(() => false);
      await queryClient.invalidateQueries({ queryKey: ["situation", id] });
      await queryClient.invalidateQueries({ queryKey: ["situations"] });
    },
  });
  useEffect(() => setNotice(null), [id]);

  if (situation.isLoading) return <Loading />;
  if (situation.error || !situation.data) return <ErrorState error={situation.error} />;
  const s = situation.data;

  return (
    <div>
      <PageHeader
        title={s.summary}
        description={t("situations.detailDescription", { id: s.situation_id, revision: s.revision })}
        sacredIcon="dove"
        actions={s.status === "active" && s.can_write ? (
          <>
            <Link to={`/situations/${s.situation_id}/edit`} className="bg-surface text-foreground border-border hover:bg-surface-elevated inline-flex h-10 items-center rounded-md border px-4 text-sm font-medium">{t("situations.edit")}</Link>
            <Button variant="danger" onClick={() => setConfirming(true)}>{t("situations.resolve")}</Button>
          </>
        ) : undefined}
      />
      {confirming ? (
        <div role="region" aria-label={t("situations.resolveTitle")} className="border-danger/40 bg-danger-soft mb-4 grid gap-3 rounded-md border p-4">
          <p className="text-sm">{t("situations.resolveBody")}</p>
          <div className="flex gap-2">
            <Button variant="danger" disabled={resolve.isPending} onClick={() => resolve.mutate(s.revision)}>{t("situations.resolveConfirm")}</Button>
            <Button variant="ghost" onClick={() => setConfirming(false)}>{t("common.cancel")}</Button>
          </div>
        </div>
      ) : null}
      {resolve.error ? <p role="alert" className="border-danger/40 bg-danger-soft text-danger mb-4 rounded-md border p-3 text-sm">{writeError(resolve.error, t)}</p> : null}
      {notice ? <p role="status" className="text-muted mb-4 text-sm">{notice}</p> : null}
      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <Card>
          <CardHeader><CardTitle sacredIcon="scroll">{t("situations.sectionMessage")}</CardTitle></CardHeader>
          <CardBody className="grid gap-3 text-sm">
            <div className="flex flex-wrap gap-2"><Badge tone={SEVERITY_TONE[s.severity]}>{t(`situations.severity.${s.severity}`)}</Badge><StatusBadge situation={s} /></div>
            {s.guidance.length > 0 ? <ul className="list-disc pl-5">{s.guidance.map((line) => <li key={line}>{line}</li>)}</ul> : <p className="text-muted">{t("situations.noGuidance")}</p>}
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
              <dt className="text-muted">{t("situations.colAudience")}</dt><dd><ScopeChips scope={s.scope} catalog={scopes.data} /></dd>
              <dt className="text-muted">{t("situations.startsAt")}</dt><dd className="tabular-nums">{formatSaoPaulo(s.starts_at)}</dd>
              <dt className="text-muted">{t("situations.expiresAt")}</dt><dd className="tabular-nums">{formatSaoPaulo(s.expires_at)}</dd>
              {s.eta ? <><dt className="text-muted">{t("situations.eta")}</dt><dd className="tabular-nums">{formatSaoPaulo(s.eta)}</dd></> : null}
              {s.policy_ref ? <><dt className="text-muted">{t("situations.policyRef")}</dt><dd>{s.policy_ref}</dd></> : null}
              <dt className="text-muted">{t("situations.actor")}</dt><dd className="min-w-0 break-all">{s.actor}</dd>
            </dl>
          </CardBody>
        </Card>
        <div className="grid content-start gap-4">
          <Card>
            <CardHeader><CardTitle sacredIcon="columns">{t("situations.sectionInternal")}</CardTitle></CardHeader>
            <CardBody className="grid gap-2 text-sm">
              <p className="text-muted text-xs">{t("situations.internalOnly")}</p>
              <p className="break-words whitespace-pre-wrap">{s.internal_note || t("situations.noNote")}</p>
            </CardBody>
          </Card>
          <Card>
            <CardHeader><CardTitle sacredIcon="book">{t("situations.revisions")}</CardTitle></CardHeader>
            <CardBody>
              {revisions.isLoading ? <Loading /> : revisions.error ? <ErrorState error={revisions.error} /> : (
                <ol className="grid gap-2 text-sm">
                  {(revisions.data?.items ?? []).map((r) => (
                    <li key={r.revision} className="grid gap-0.5">
                      <span className="flex gap-1"><span className="tabular-nums">#{r.revision}</span><span aria-hidden="true">·</span><span>{t(REVISION_TYPE_KEY[r.type] ?? "situations.revision.other")}</span></span>
                      <span className="text-muted min-w-0 break-all text-xs">{formatSaoPaulo(r.time ?? r.recorded_at)} · {r.actor ?? "—"}</span>
                    </li>
                  ))}
                </ol>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
